/**
 * Ukrainian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: ви, lowercase (locked in l10n/locales.md).
 *
 * Platform terms follow Ukrainian UI conventions (налаштування,
 * створення пари for pairing, дублювання for notification mirroring,
 * видалити for dismiss, розмова for conversation, приймач for surface).
 * "Glance" is the untranslated feature brand name (glossary D1); «кліп»
 * renders the D2 loanword in Cyrillic (locked CJK/Cyrillic clarification).
 *
 * Plural notes: Ukrainian is a {one, few, many, other} CLDR locale —
 * every count-bearing `selectPlural("uk", …)` supplies all four forms.
 * `one` covers 1, 21, 31, 41… (not just 1), so numbers are never
 * hardcoded in the `one` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Підключено",
    statusReconnecting: "Перепідключення…",
    statusDisconnected: "Відключено",
    openSettings: "Відкрити налаштування",
    pairedFallbackDevice: "ваш телефон",
    publicModeToggleEnable: "Приховати вміст сповіщень",
    publicModeToggleDisable: "Показати вміст сповіщень",
  },

  notifications: {
    replyAriaLabel: "Відповісти",
    replyPlaceholder: "Відповісти…",
    replySend: "Надіслати",
    sent: "Надіслано",
    failedToSend: "Не вдалося надіслати",
    unread: "Непрочитане",
    dismiss: "Видалити",
    notificationImageAlt: "Зображення зі сповіщення",
  },

  conversations: {
    replyAriaLabel: "Відповісти",
    replyPlaceholder: "Відповісти…",
    replySend: "Надіслати",
    sent: "Надіслано",
    failedToSend: "Не вдалося надіслати",
    dismiss: "Видалити розмову",
    messagesListLabel: "Повідомлення в цій розмові",
    deviceOwner: "Ви",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Немає непрочитаних повідомлень"
        : `${count} ${selectPlural("uk", count, {
            one: "непрочитане повідомлення",
            few: "непрочитані повідомлення",
            many: "непрочитаних повідомлень",
            other: "непрочитаного повідомлення",
          })}`,
  },

  list: {
    today: "Сьогодні",
    yesterday: "Вчора",
    searchNotifications: "Пошук сповіщень",
    clearSearch: "Очистити пошук",
    clearAll: "Видалити все",
    markAllRead: "Позначити все прочитаним",
    groupCheckboxLabel: "Групувати",
    noNotificationsTitle: "Сповіщень ще немає",
    noNotificationsDesc: "Нові сповіщення, продубльовані з телефону, з'являтимуться тут.",
    noMatchesTitle: "Нічого не знайдено",
    notificationsList: "Сповіщення",
    privacyAppList: "Сповіщення за застосунками",
    clear: "Видалити",
    clearMatches: (count: number): string =>
      `Видалити ${count} ${selectPlural("uk", count, {
        one: "збіг",
        few: "збіги",
        many: "збігів",
        other: "збігу",
      })}`,
    noMatchesDesc: (query: string): string => `Немає сповіщень за запитом «${query}».`,
    appNotificationsGroup: (appName: string): string => `Сповіщення ${appName}`,
    dismissAllFrom: (appName: string): string => `Видалити всі від ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("uk", count, {
              one: "повідомлення",
              few: "повідомлення",
              many: "повідомлень",
              other: "повідомлення",
            })
          : kind === "calls"
            ? selectPlural("uk", count, {
                one: "виклик",
                few: "виклики",
                many: "викликів",
                other: "виклику",
              })
            : selectPlural("uk", count, {
                one: "сповіщення",
                few: "сповіщення",
                many: "сповіщень",
                other: "сповіщення",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "видалити",
    kbdNavigate: "навігація",
    kbdOrSeparator: "або",
  },

  settings: {
    back: "Назад до сповіщень",
    settings: "Налаштування",
    appearance: "Зовнішній вигляд",
    theme: "Тема",
    themeAuto: "Автоматична",
    themeLight: "Світла",
    themeDark: "Темна",
    language: "Мова",
    languageSystemDefault: "Як у системі",
    notificationsSection: "Сповіщення",
    osNotifications: "Системні сповіщення",
    callNotifications: "Сповіщення про виклики",
    privacySection: "Конфіденційність",
    analyticsToggle: "Анонімна статистика використання",
    analyticsToggleDescription: "Лише лічильники та етапи — ніколи ваші сповіщення.",
    connection: "Підключення",
    status: "Статус",
    statusConnected: "Підключено",
    statusReconnecting: "Перепідключення…",
    statusDisconnected: "Відключено",
    disconnectFromPhone: "Відключити від телефону",
    disconnectConfirm: "Ви впевнені? Пару з пристроєм буде розірвано.",
    yesDisconnect: "Так, відключити",
    cancel: "Скасувати",
    aboutAndSupport: "Про застосунок і підтримка",
    version: "Версія",
    encryption: "Шифрування",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Потрібна допомога? Напишіть на __EMAIL__",
    disconnectFailed: "Не вдалося відключити — спробуйте ще раз",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Відключено, але не вдалося очистити локальні дані: ${errorMsg}. Перезавантажте сторінку.`,
    disconnectLocalCleanupFailed:
      "Відключено, але не вдалося очистити локальні дані. Перезавантажте сторінку.",
  },

  pairing: {
    enterCodeInstruction: "Введіть цей код у застосунку Reflecto",
    pairedSuccessfully: "Пару успішно створено!",
    pairingFailed: "Не вдалося створити пару",
    requestingPairingCode: "Запитуємо код для створення пари…",
    codeExpired: "Термін дії коду сплив",
    tryAgain: "Спробувати ще раз",
    failedToStartPairing: "Не вдалося почати створення пари",
    needAppText: "Потрібен застосунок для Android? Напишіть на __EMAIL__, щоб отримати доступ.",
  },

  dnd: {
    title: "Не турбувати",
    description: "Призупинити сповіщення на цьому пристрої на найближчу годину.",
    snooze1h: "Призупинити на 1 годину",
    snoozeActiveUntil: (formattedTime: string): string => `Активно до ${formattedTime}`,
    cancelSnooze: "Скасувати",
  },

  connectors: {
    title: "Підключені сервіси",
    emptyBody:
      "Підключених сервісів поки немає. Підключіть ChatGPT або Claude в налаштуваннях їхніх конекторів, а потім підтвердьте код на телефоні.",
    scopeSend: "Може надсилати сповіщення",
    added: (date: string): string => `Додано ${date}`,
    lastUsed: (relative: string): string => `Востаннє використано ${relative}`,
    neverUsed: "Не використовувався",
    revoke: "Відключити",
    revokeConfirm: (label: string): string => `Відключити ${label}? Доступ буде втрачено негайно.`,
    revokeAction: "Відключити",
    revokeConfirmLabel: (label: string): string => `Відключити ${label}`,
    cancel: "Скасувати",
    loadError: "Не вдалося завантажити підключені сервіси. Спробуйте ще раз.",
    revokeError: "Не вдалося відключити сервіс. Спробуйте ще раз.",
    retry: "Повторити",
  },

  feedback: {
    title: "Надіслати відгук",
    close: "Закрити",
    messageLabel: "Ваше повідомлення",
    messagePlaceholder: "Знайшли баг, маєте ідею чи щось не так? Розкажіть нам.",
    categoryLabel: "Категорія",
    categoryBug: "Баг",
    categoryIdea: "Ідея",
    categoryOther: "Інше",
    emailLabel: "Ел. пошта (необов'язково, лише якщо хочете отримати відповідь)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Ми не додамо вас до жодних розсилок.",
    disclosure:
      "Ваше повідомлення надсилається на сервер Reflecto і зберігається в нашому внутрішньому трекері задач. Ми додаємо версію застосунку та ідентифікатор пристрою, наданий під час створення пари, щоб зіставляти звіти. Ми не бачимо ваші сповіщення, контакти чи інші особисті дані. Ел. пошта — за бажанням: вкажіть її, лише якщо хочете отримати відповідь. Ми не додамо вас до жодних розсилок.",
    submit: "Надіслати відгук",
    sending: "Надсилаємо…",
    success: "Дякуємо — відгук прочитає людина, зазвичай протягом 24 год",
    error: "Не вдалося надіслати. Спробуйте ще раз.",
    done: "Готово",
    sendFeedback: "Надіслати відгук",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Закрити",
    aiSummaryTag: "Зведення ШІ",
    errorAi: "Зараз не вдалося скласти зведення.",
    retryCta: "Спробувати ще раз",
    downloadHeading: "Зведення від ШІ",
    downloadBody:
      "Надолужте пропущене за допомогою зведення від ШІ прямо на пристрої. Усе працює лише на вашому пристрої.",
    downloadCta: "Завантажити модель",
    downloadPrivacyNote: "Одноразове завантаження. Сервер ніколи не бачить ваші сповіщення.",
    downloadingHeading: "Завантажуємо модель…",
    caughtUp: "Ви все переглянули — зараз ніщо не потребує вашої уваги.",
    needsYou: "Потребує уваги",
    quiet: "Решта",
    scanning: (pending: number): string => `Готуємо зведення: ${pending}…`,
    jump: "Відкрити сповіщення",
    affordanceReply: "Відповісти",
    affordanceOpen: "Відкрити",
    affordanceCopyOtp: "Копіювати код",
    buttonLabel: "Glance",
  },

  update: {
    title: "Доступне оновлення",
    description: "Готова нова версія Reflecto. Перезавантажте сторінку, щоб оновитися.",
    reload: "Перезавантажити",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Ми назвали цей пристрій «${name}».`,
    cardLooksGood: "Підходить",
    cardEdit: "Змінити",
    cardAriaLabel: "Підтвердьте назву пристрою",
    renameTitle: "Перейменувати цей пристрій",
    renameLabel: "Назва пристрою",
    renameSave: "Зберегти",
    renameCancel: "Скасувати",
    renameFailed: "Не вдалося перейменувати. Спробуйте ще раз.",
  },

  collision: {
    title: (name: string): string => `У вас уже є «${name}»`,
    subtitle: "Як назвати цей пристрій?",
    chipWork: (name: string): string => `Робочий ${name}`,
    chipHome: (name: string): string => `Домашній ${name}`,
    chipPersonal: (name: string): string => `Особистий ${name}`,
    customLabel: "Власна назва",
    save: "Зберегти",
    useAnyway: "Усе одно використати початкову назву",
    saveFailed: "Не вдалося зберегти нову назву. Спробуйте ще раз.",
    ariaLabel: "Усуньте збіг назв пристроїв",
  },

  clipboard: {
    title: "Буфер обміну",
    empty: "Кліпи з інших ваших пристроїв з'являються тут. Натисніть, щоб скопіювати.",
    from: (label: string): string => `від ${label}`,
    copy: "Копіювати в буфер обміну",
    copied: "Скопійовано",
    reveal: "Показати",
    hide: "Приховати",
    maskedHint: "Вміст приховано — натисніть «Показати»",
    sensitiveChip: "Конфіденційно",
    cloudChip: "через ваш токен API",
    sendPlaceholder: "Вставте текст для надсилання…",
    sendCta: "Надіслати",
    sendSensitiveToggle: "Позначити як конфіденційний",
    sendSuccess: (n: number): string =>
      `Надіслано на ${n} ${selectPlural("uk", n, {
        one: "пристрій",
        few: "пристрої",
        many: "пристроїв",
        other: "пристрою",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Надіслано на ${n} з ${total} ${selectPlural("uk", total, {
        one: "пристрою",
        few: "пристроїв",
        many: "пристроїв",
        other: "пристрою",
      })}.`,
    sendNoDevices: "Немає спарованих пристроїв для надсилання.",
    sendFailed: "Не вдалося надіслати. Спробуйте ще раз.",
    sendEmpty: "Немає чого надсилати.",
    dockExpand: "Показати історію кліпів",
    dockCollapse: "Згорнути",
    dockSendTitle: "Надіслати на ваші пристрої",
    dockFromClipboard: (chars: number): string =>
      `З буфера обміну · ${chars} ${selectPlural("uk", chars, {
        one: "символ",
        few: "символи",
        many: "символів",
        other: "символа",
      })}`,
    dockSend: (n: number): string => `Надіслати на ${n}`,
    dockCancel: "Скасувати",
    dockRecentHeading: "Нещодавні",
    dockEmptyClipboard: "Буфер обміну порожній.",
    shareTitle: "Надіслати на ваші пристрої",
    shareReview:
      "Перевірте, що надсилаєте. Дані підуть на ваші спаровані пристрої з наскрізним шифруванням.",
    shareSend: "Надіслати",
    shareCancel: "Скасувати",
    shareNotPaired: "Щоб надіслати спільний текст, спочатку створіть пару з пристроєм.",
    shareOpenApp: "Відкрити Reflecto",
  },
  install: {
    title: "Встановити Reflecto",
    body: "Додайте на головний екран для швидшого доступу.",
    cta: "Встановити",
    dismissAria: "Відхилити запит на встановлення",
  },
  common: {
    loading: "Завантаження",
  },
  errors: {
    notPaired: "Не спаровано",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} на ${os}`,
    webFallback: "Веб-браузер",
  },

  palette: {
    placeholder: "Введіть команду…",
    noResults: "Немає відповідних команд",
    ariaLabel: "Палітра команд",
    clearAll: "Видалити всі сповіщення",
    glance: "Зведення Glance",
    cancelSnooze: "Скасувати призупинення",
    copyClip: "Копіювати останній отриманий кліп",
  },
} satisfies MessagesShape;
